import React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API, LineupPill, StatusPill, na, pct } from "@/components/fbShared";

const Box = ({ title, children, testId }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-2.5" data-testid={testId}>
    <p className="mb-1 font-mono text-[9px] font-bold uppercase tracking-wider text-orange-400">{title}</p>{children}
  </div>
);
const Line = ({ k, v }) => <p className="flex justify-between gap-2 font-mono text-[10px]"><span className="text-slate-500">{k}</span><span className="text-slate-200">{v}</span></p>;
const rate = (o) => (o && o.starts ? `${o.fb}/${o.starts} (${na(o.rate, "%")})` : "N/A");

const Components = ({ c, labels }) => (
  <Box title={`Fusion ${na(c.fusion)} · ${Math.round(c.completeness * 100)}% of weight available`} testId="fb-drawer-components">
    {Object.entries(c.components).map(([k, v]) => (
      <div key={k} className="mb-1">
        <div className="flex justify-between font-mono text-[10px]"><span className="text-slate-400">{labels[k]} · {c.weights[k]}%</span><span className="text-slate-100">{na(v)}</span></div>
        <div className="h-1 rounded bg-slate-800"><div className="h-1 rounded bg-orange-400" style={{ width: `${v || 0}%` }} /></div>
      </div>
    ))}
    <p className="mt-1 font-mono text-[9px] text-slate-500">Fusion is a 0-100 ranking index, not a probability. Missing components are left out and the remaining weights re-scaled.</p>
  </Box>
);

const Body = ({ d }) => {
  const c = d.candidate, p = d.profile, t = d.tip, o = d.opponent;
  return (
    <div className="space-y-2.5">
      {c && (
        <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-slate-400">
          <span>{c.team} {c.pos || "N/A"} vs {c.opp}</span><LineupPill s={c.starter} /><StatusPill s={c.status} testId="fb-drawer-status" />
          <span>est {c.prob_calibrated ? pct(c.prob) : "not calibrated"} · sportsbook implied N/A (no odds)</span>
          {c.archetypes.map((a) => <span key={a} className="rounded bg-slate-800 px-1.5 text-slate-300">{a}</span>)}
        </div>
      )}
      {c && <Components c={c} labels={d.labels} />}
      {c && (
        <Box title="Why this ranking" testId="fb-drawer-reasons">
          <p className="mb-1 font-mono text-[10px] font-bold text-orange-200" data-testid="fb-drawer-status-reason">{c.status}: {c.status_reason}</p>
          {c.reasons.map((r) => <p key={r} className="font-mono text-[10px] text-slate-300">• {r}</p>)}
          {c.missing.map((r) => <p key={r} className="font-mono text-[10px] text-amber-300">• missing: {r}</p>)}
          {c.warnings.map((r) => <p key={r} className="font-mono text-[10px] text-amber-300">• {r}</p>)}
          <div className="mt-1 flex flex-wrap gap-1">{Object.entries(c.gates).map(([k, v]) => <span key={k} className={`rounded px-1.5 font-mono text-[9px] ${v ? "bg-emerald-950/60 text-emerald-300" : "bg-rose-950/50 text-rose-300"}`}>{v ? "✓" : "✗"} {k.replace(/_/g, " ")}</span>)}</div>
        </Box>
      )}
      {p ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Box title="First-basket history (starts)" testId="fb-drawer-history">
            <Line k="Stored window" v={rate(p.window)} /><Line k="This season" v={rate(p.season)} /><Line k="Previous season" v={rate(p.prev)} />
            <Line k="Career" v="N/A (outside stored window)" />
            {Object.entries(p.windows).map(([k, w]) => <Line key={k} k={k} v={`${w.hits}/${w.n}`} />)}
            <Line k="Home / away" v={`${rate(p.home)} · ${rate(p.away)}`} /><Line k="First FG of game" v={`${p.ffg}/${p.starts}`} />
            <Line k="Share of team's first baskets" v={`${pct(p.team_share)} (${p.team_first_starts} games)`} />
          </Box>
          <Box title="Opening play & first shot" testId="fb-drawer-opening">
            <Line k="Team's first shot" v={`${p.first_shot}/${p.starts}`} /><Line k="First-shot conversion" v={na(p.first_shot_conv, "%")} />
            <Line k="Received the opening tip" v={`${p.tip_gains}/${p.starts}`} />
            <p className="mt-1 font-mono text-[9px] text-slate-500">Screens, rolls and set plays are not in play-by-play: UNKNOWN.</p>
            <p className="mt-1 font-mono text-[9px] uppercase text-slate-500">First-shot methods</p>
            {Object.entries(p.first_shot_methods).map(([k, v]) => <Line key={k} k={k} v={v} />)}
            <p className="mt-1 font-mono text-[9px] uppercase text-slate-500">First-basket methods</p>
            {Object.keys(p.fb_methods).length ? Object.entries(p.fb_methods).map(([k, v]) => <Line key={k} k={k} v={v} />) : <p className="font-mono text-[10px] text-slate-600">none</p>}
          </Box>
        </div>
      ) : <p className="font-mono text-[11px] text-amber-300">No stored starts for this player — all history fields N/A.</p>}
      <Box title="Tip-off record" testId="fb-drawer-tip">
        {t.attempts ? <><Line k="Verified" v={`${t.wins}-${t.losses} (${na(t.pct, "%")})`} /><Line k="Recent" v={`${t.recent_wins}/${t.recent_attempts}`} />
          {t.log.map((x, i) => <p key={i} className="font-mono text-[10px] text-slate-400">{x.date} · {x.won ? "W" : "L"} vs {x.opp}</p>)}</> : <p className="font-mono text-[10px] text-slate-500">No recorded jump balls (N/A).</p>}
      </Box>
      {o && c && (
        <Box title={`${c.opp} first baskets allowed by position`} testId="fb-drawer-opp">
          {Object.entries(o.index).map(([k, v]) => <Line key={k} k={`${k}${k === c.pos ? " ◀" : ""}`} v={`${pct(v.shrunk)} vs league ${pct(v.league)} · ${na(v.index, "x")}`} />)}
          <p className="mt-1 font-mono text-[9px] text-slate-500">{o.opp_first} opponent-first games of {o.games}; 60/25/15 blend of L20/L10/L5, shrunk toward league.</p>
        </Box>
      )}
      {p && (
        <Box title="Recent starts" testId="fb-drawer-log">
          {p.log.map((x, i) => (
            <p key={i} className="flex gap-2 font-mono text-[10px]"><span className="text-slate-500">{x.date}</span><span className="w-14 text-slate-400">{x.home ? "vs" : "@"} {x.opp}</span>
              <span className={x.fb ? "font-bold text-orange-300" : "text-slate-500"}>{x.fb ? `FIRST BASKET · ${x.method}` : x.first_shot ? `1st shot: ${x.shot}` : "—"}</span></p>
          ))}
        </Box>
      )}
    </div>
  );
};

export const PlayerDrawer = ({ pid, date, onClose }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    if (!pid) return undefined;
    let live = true;
    setD(null);
    axios.get(`${API}/player/${pid}`, { params: { date } }).then((r) => live && setD(r.data)).catch(() => live && setD({ error: true }));
    return () => { live = false; };
  }, [pid, date]);
  return (
    <Sheet open={!!pid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-slate-800 bg-slate-950 text-slate-100 sm:max-w-xl" data-testid="fb-player-drawer">
        <SheetHeader><SheetTitle className="font-display uppercase text-orange-200">{d?.candidate?.name || d?.profile?.name || "Player"}</SheetTitle></SheetHeader>
        {!d ? <div className="flex items-center gap-2 py-8 font-mono text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-orange-400" /> Loading…</div>
          : d.error ? <p className="font-mono text-xs text-rose-400">Player not available.</p> : <Body d={d} />}
      </SheetContent>
    </Sheet>
  );
};
